import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useNutzerUnternehmen } from '@/auftraggeber/features/NutzerUnternehmen.ts';
import { useAuftraggeberApi } from '@/auftraggeber/api/useAuftraggeberApi.ts';
import { createEmptyNutzer, createEmptyRolle, type Rolle } from '@/types/Nutzer.ts';
import {
  KonfigurationsTyp,
  NutzerAuswahlTyp,
  RollenAuswahlTyp,
  type ZweitpruefungKonfigurationRequest,
} from '@/auftraggeber/types/Einstellung.ts';

export const useMehrAugenPrinzipStore = defineStore('mehrAugenPrinzip', () => {
  const isAuftragZweitpruefungActive = ref<boolean>(false);

  const rollenAuswahlTyp = ref<RollenAuswahlTyp>(null);
  const selectedRolleIds = ref<string[]>([]);
  const neueRolle = ref<Rolle>(createEmptyRolle());

  const nutzerAuswahlTyp = ref<NutzerAuswahlTyp>(null);
  const selectedNutzerId = ref<string[]>([]);
  const neuerNutzer = ref(createEmptyNutzer());

  const ladeMehrAugenPrinzip = async () => {
    const organisation = useNutzerUnternehmen().unternehmen;
    isAuftragZweitpruefungActive.value = organisation?.auftragZweitpruefungAktiv ?? false;
  };

  const setzeAuftragZweitpruefung = async (
    aktiv: boolean,
    konfiguration?: ZweitpruefungKonfigurationRequest,
  ) => {
    await useAuftraggeberApi().partner.updateZweitpruefungAuftrag(aktiv, konfiguration);
    useNutzerUnternehmen().unternehmen.auftragZweitpruefungAktiv = aktiv;
    isAuftragZweitpruefungActive.value = aktiv;
  };

  const aktiviereMehrAugenPrinzip = async () => {
    await setzeAuftragZweitpruefung(true, baueKonfiguration());
  };

  const baueKonfiguration = (): ZweitpruefungKonfigurationRequest => {
    if (rollenAuswahlTyp.value === RollenAuswahlTyp.NEU) {
      return {
        konfigurationsTyp: KonfigurationsTyp.NEUE_ROLLE,
        neueRolle: {
          name: neueRolle.value.name,
          iconname: neueRolle.value.iconname,
          immutable: false,
          rechte: neueRolle.value.rechte.map((r) => r.name),
          nutzer: {
            typ: nutzerAuswahlTyp.value,
            neuerNutzer:
              nutzerAuswahlTyp.value === NutzerAuswahlTyp.NEU ? neuerNutzer.value : undefined,
            nutzerIds:
              nutzerAuswahlTyp.value === NutzerAuswahlTyp.BESTEHEND
                ? selectedNutzerId.value
                : undefined,
          },
        },
      };
    }
    return {
      konfigurationsTyp: KonfigurationsTyp.BESTEHENDE_ROLLEN,
      bestehendeRollenIds: selectedRolleIds.value,
    };
  };

  const $reset = () => {
    rollenAuswahlTyp.value = null;
    selectedRolleIds.value = [];
    neueRolle.value = createEmptyRolle();
    nutzerAuswahlTyp.value = null;
    selectedNutzerId.value = [];
    neuerNutzer.value = createEmptyNutzer();
  };

  return {
    aktiviereMehrAugenPrinzip,
    isAuftragZweitpruefungActive,
    ladeMehrAugenPrinzip,
    setzeAuftragZweitpruefung,
    rollenAuswahlTyp,
    selectedRolleIds,
    neueRolle,
    nutzerAuswahlTyp,
    selectedNutzerId,
    neuerNutzer,
    $reset,
  };
});
